/**
 * [TEXT-TRACE] Redux middleware.
 *
 * After every aiAnalysis/* action, diffs each action's effective displayed text
 * (improvementPatch?.improvedQuestion ?? highlightedQuestion ?? details) across
 * suggestedActions/queuedActions/allSuggestedActions, before vs after the reducer,
 * and logs only the ones that CHANGED — tagged with the causing redux action type
 * (and payload.__source when dispatch sites set it). This catches every
 * reducer-driven flap automatically (setImprovementPatch, hydrateCanonicalActionProjection,
 * setAiAnalysisPayload, updateAnswerDetails, the reapplyImprovementOverlay effect, etc.).
 *
 * No-op unless tracing is enabled. See lib/textTrace.ts.
 */

import type { Middleware } from '@reduxjs/toolkit';
import { textTraceEnabled, effDisplay, trace } from '@/lib/textTrace';

const idText = (state: any): Map<string, string> => {
  const m = new Map<string, string>();
  const ai = state?.aiAnalysis;
  if (!ai) return m;
  for (const arr of [ai.suggestedActions, ai.queuedActions, ai.allSuggestedActions]) {
    if (!Array.isArray(arr)) continue;
    for (const a of arr) {
      if (a?.id && !m.has(a.id)) m.set(a.id, effDisplay(a));
    }
  }
  return m;
};

export const textTraceMiddleware: Middleware = (store) => (next) => (action: any) => {
  if (
    !textTraceEnabled() ||
    typeof action?.type !== 'string' ||
    !action.type.startsWith('aiAnalysis/')
  ) {
    return next(action);
  }
  const before = idText(store.getState());
  const result = next(action);
  const after = idText(store.getState());
  const src = action.payload?.__source;
  for (const [id, newText] of after) {
    const oldText = before.get(id);
    if (oldText !== undefined && oldText !== newText) {
      trace({
        site: 'reducer',
        actionId: id,
        oldText,
        newText,
        changedField: 'effDisplay',
        cause: action.type + (src ? ` (${src})` : ''),
      });
    }
  }
  return result;
};
